/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { CHAR_HEIGHT, CHAR_WIDTH } from './Axis';
import { useTimeCursorHandlers } from './useTimeCursorHandlers';

export function useSvgLayout({
  top = CHAR_HEIGHT / 2,
  right = 24,
  bottom = 32,
  left = 16,
}: {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}) {
  const [maxYLabelLength, setMaxYLabelLength] = useState(0);
  const [maxLeftYLabelLength, setMaxLeftYLabelLength] = useState(0);
  const [_firstTick, setFirstTick] = useState({ labels: [''], offset: 0 });

  const containerSize = useResizeDetector();
  const rightMargin = right + maxYLabelLength * CHAR_WIDTH;
  const leftMargin = left + maxLeftYLabelLength * CHAR_WIDTH;

  return {
    height: Math.max(0, (containerSize.height ?? 0) - top - bottom),
    width: Math.max(0, (containerSize.width ?? 0) - leftMargin - rightMargin),
    ref: containerSize.ref,
    x: leftMargin,
    y: top,
    setMaxYLabelLength,
    setMaxLeftYLabelLength,
    setFirstTick,
    bottom,
  };
}
export const SvgWrapper = React.forwardRef<
  HTMLDivElement,
  {
    className?: string;
    children: React.ReactNode;
    x: number;
    y: number;
    height: number;
    width: number;
    getCursor: Parameters<typeof useTimeCursorHandlers>[0];
    canvasRef?: React.MutableRefObject<HTMLCanvasElement | null>;
    onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
    'data-testid'?: string;
  }
>(
  (
    {
      className,
      children,
      x,
      y,
      height,
      width,
      getCursor,
      canvasRef,
      onMouseEnter,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const cursorHandlers = useTimeCursorHandlers(getCursor);
    return (
      <div
        className={classnames(className, 'relative')}
        ref={ref}
        onMouseEnter={onMouseEnter}
        data-testid={dataTestId}
      >
        {canvasRef && (
          <canvas
            className="absolute"
            ref={canvasRef}
            css={css`
              width: ${width}px;
              height: ${height}px;
              left: ${x}px;
              top: ${y}px;
            `}
          />
        )}
        <svg width="100%" height="100%" className="relative">
          <g transform={`translate(${x}, ${y})`} {...cursorHandlers}>
            <rect height={height} width={width} fill="transparent" />
            {children}
          </g>
        </svg>
      </div>
    );
  },
);
